import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Breadcrumbs,
    Link,
    Paper,
    FormControl,
    Select,
    MenuItem,
    TextField,
    Button,
    Divider,
    Chip,
    Stack,
    InputAdornment,
    useTheme,
    Skeleton,
    Pagination,
    IconButton,
    Drawer,
    Collapse,
    Badge,
    Slider,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    FilterList,
    Sort,
    SearchOff,
    ChevronRight,
    Home as HomeIcon,
    ClearAll,
    Search as SearchIcon,
    Close,
    ExpandMore,
    ExpandLess,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ProductCard from '../../components/storefront/home/shared/ProductCard';
import { useSearchProducts } from '../../hooks/storefront/useSearchProducts';
import { useGetFilterOptions } from '../../hooks/storefront/useGetFilterOptions';
import { formatMoney } from '../../utils/money';

const Search = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const lng = i18n.language || 'en';

    // UI State
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        departments: true,
        categories: true,
        brands: true,
        tags: true,
        price: true,
    });
    const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

    // Filters from URL
    const q = searchParams.get('q') || '';
    const departmentSlugs = searchParams.get('department_slugs') || '';
    const categorySlugs = searchParams.get('category_slugs') || '';
    const brandSlugs = searchParams.get('brand_slugs') || '';
    const tagSlugs = searchParams.get('tag_slugs') || '';
    const page = parseInt(searchParams.get('page') || '1');
    const sort = searchParams.get('sort') || 'newest';
    const minPrice = searchParams.get('min_price') || '';
    const maxPrice = searchParams.get('max_price') || '';
    const productType = searchParams.get('product_type') || '';
    const inStockOnly = searchParams.get('in_stock') === '1';
    const perPage = parseInt(searchParams.get('per_page') || '24');

    // Data Fetching
    const { isLoading, searchResults, pagination } = useSearchProducts({
        q,
        department_slugs: departmentSlugs,
        category_slugs: categorySlugs,
        brand_slugs: brandSlugs,
        tag_slugs: tagSlugs,
        page,
        sort,
        min_price: minPrice,
        max_price: maxPrice,
        product_type: productType || undefined,
        in_stock: inStockOnly ? 1 : undefined,
        per_page: perPage,
    });

    const { data: filterOptions } = useGetFilterOptions();

    const departments = useMemo(() => filterOptions?.departments || [], [filterOptions]);
    const allCategories = useMemo(() => filterOptions?.categories || [], [filterOptions]);
    const allBrands = useMemo(() => filterOptions?.brands || [], [filterOptions]);
    const allTags = useMemo(() => filterOptions?.tags || [], [filterOptions]);

    // Handlers
    const updateParams = (updates) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        newParams.set('page', '1'); // Reset to first page on filter change
        setSearchParams(newParams);
    };

    const handleSortChange = (e) => updateParams({ sort: e.target.value });

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        updateParams({ q: searchInput });
    };

    // Auto-trigger search with debounce (~400ms) as the user types
    useEffect(() => {
        if (searchInput === q) return;
        const timer = setTimeout(() => {
            updateParams({ q: searchInput });
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    // Helper to toggle comma-separated items
    const toggleFilter = (currentStr, value) => {
        const items = currentStr ? currentStr.split(',') : [];
        if (items.includes(value)) {
            return items.filter(item => item !== value).join(',');
        }
        return [...items, value].join(',');
    };

    const handleDepartmentToggle = (slug) => {
        updateParams({ department_slugs: toggleFilter(departmentSlugs, slug) });
    };

    const handleCategoryToggle = (slug) => {
        updateParams({ category_slugs: toggleFilter(categorySlugs, slug) });
    };

    const handleBrandToggle = (slug) => {
        updateParams({ brand_slugs: toggleFilter(brandSlugs, slug) });
    };

    const handleTagToggle = (slug) => {
        updateParams({ tag_slugs: toggleFilter(tagSlugs, slug) });
    };

    const clearAllFilters = () => {
        setSearchParams(q ? { q } : {});
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const activeFiltersCount = [
        departmentSlugs, categorySlugs, brandSlugs, tagSlugs,
        minPrice, maxPrice, productType,
        inStockOnly ? '1' : '',
    ].filter(Boolean).length;

    // Price slider bounds — derive from filter options when the
    // backend exposes them, otherwise fall back to a sane default.
    const priceBounds = useMemo(() => {
        const lo = Number(filterOptions?.price_range?.min) || 0;
        const hi = Number(filterOptions?.price_range?.max) || 1000;
        return { lo, hi: hi > lo ? hi : lo + 1 };
    }, [filterOptions]);
    const sliderValue = [
        minPrice === '' ? priceBounds.lo : Number(minPrice),
        maxPrice === '' ? priceBounds.hi : Number(maxPrice),
    ];

    // Filter Logic
    const availableCategories = useMemo(() => {
        if (!departmentSlugs) return allCategories;
        const selectedDepts = departmentSlugs.split(',');
        const deptIds = departments
            .filter(d => selectedDepts.includes(d.slug))
            .map(d => d.id);
        return allCategories.filter(c => deptIds.includes(c.department_id));
    }, [departmentSlugs, departments, allCategories]);

    const availableBrands = useMemo(() => {
        let targetCategories = availableCategories;

        if (categorySlugs) {
            const selectedSlugs = categorySlugs.split(',');
            targetCategories = allCategories.filter(c => selectedSlugs.includes(c.slug));
        }

        const targetCategoryIds = targetCategories.map(c => c.id);

        return allBrands.filter(b =>
            b.category_ids?.some(cid => targetCategoryIds.includes(cid))
        );
    }, [categorySlugs, availableCategories, allBrands, allCategories]);

    // Filter Section Component
    const FilterSection = ({ title, items, selectedSlugs, onToggle, sectionKey }) => (
        <Box sx={{ mb: 2 }}>
            <Box
                onClick={() => toggleSection(sectionKey)}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    py: 1.5,
                    px: 1,
                    mx: -1,
                    borderRadius: 1,
                    '&:hover': { backgroundColor: theme.palette.action.hover }
                }}
            >
                <Typography variant="subtitle2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
                    {title}
                </Typography>
                {expandedSections[sectionKey] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
            <Collapse in={expandedSections[sectionKey]}>
                <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 280, overflowY: 'auto', pr: 1 }}>
                    {items.map((item) => {
                        const isSelected = selectedSlugs.split(',').includes(item.slug);
                        return (
                            <Box
                                key={item.id}
                                onClick={() => onToggle(item.slug)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    p: 1,
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s',
                                    backgroundColor: isSelected ? theme.palette.primary.main + '10' : 'transparent',
                                    '&:hover': { backgroundColor: isSelected ? theme.palette.primary.main + '20' : theme.palette.action.hover }
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color={isSelected ? 'primary.main' : 'text.primary'}
                                    fontWeight={isSelected ? '600' : '400'}
                                    sx={{ fontSize: '0.875rem' }}
                                >
                                    {item.name?.[lng] || item.name?.en || item.name}
                                </Typography>
                                {isSelected && (
                                    <Box sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main'
                                    }} />
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Collapse>
        </Box>
    );

    // Filters Content
    const FiltersContent = () => (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterList sx={{ fontSize: 22 }} />
                    {t('search.filters')}
                </Typography>
                {activeFiltersCount > 0 && (
                    <Button
                        size="small"
                        onClick={clearAllFilters}
                        startIcon={<ClearAll />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                        }}
                    >
                        {t('search.clearAll')}
                    </Button>
                )}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Department Filter */}
            {departments.length > 0 && (
                <FilterSection
                    title={t('search.departments')}
                    items={departments}
                    selectedSlugs={departmentSlugs}
                    onToggle={handleDepartmentToggle}
                    sectionKey="departments"
                />
            )}

            {/* Category Filter */}
            {availableCategories.length > 0 && (
                <FilterSection
                    title={t('search.categories')}
                    items={availableCategories}
                    selectedSlugs={categorySlugs}
                    onToggle={handleCategoryToggle}
                    sectionKey="categories"
                />
            )}

            {/* Brand Filter */}
            {availableBrands.length > 0 && (
                <FilterSection
                    title={t('search.brands')}
                    items={availableBrands}
                    selectedSlugs={brandSlugs}
                    onToggle={handleBrandToggle}
                    sectionKey="brands"
                />
            )}

            {/* Tags Filter */}
            {allTags.length > 0 && (
                <FilterSection
                    title={t('search.tags')}
                    items={allTags}
                    selectedSlugs={tagSlugs}
                    onToggle={handleTagToggle}
                    sectionKey="tags"
                />
            )}

            {/* Price Range — slider replaces the old min/max pair */}
            <Box sx={{ mb: 2 }}>
                <Box
                    onClick={() => toggleSection('price')}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        py: 1.5,
                        px: 1,
                        mx: -1,
                        borderRadius: 1,
                        '&:hover': { backgroundColor: theme.palette.action.hover }
                    }}
                >
                    <Typography variant="subtitle2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
                        {t('search.priceRange')}
                    </Typography>
                    {expandedSections.price ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
                <Collapse in={expandedSections.price}>
                    <Box sx={{ mt: 2, px: 1 }}>
                        <Slider
                            value={sliderValue}
                            min={priceBounds.lo}
                            max={priceBounds.hi}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => formatMoney(v)}
                            onChange={(_, newValue) => {
                                // Local UX only — don't spam the URL on every drag tick.
                            }}
                            onChangeCommitted={(_, newValue) => {
                                const [lo, hi] = Array.isArray(newValue) ? newValue : [newValue, newValue];
                                updateParams({
                                    min_price: lo > priceBounds.lo ? String(lo) : null,
                                    max_price: hi < priceBounds.hi ? String(hi) : null,
                                });
                            }}
                        />
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                                {formatMoney(sliderValue[0])}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatMoney(sliderValue[1])}
                            </Typography>
                        </Stack>
                    </Box>
                </Collapse>
            </Box>

            {/* Product type */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="700" sx={{ fontSize: '0.85rem', mb: 1 }}>
                    {t('search.productType', 'Product type')}
                </Typography>
                <Select
                    size="small"
                    fullWidth
                    value={productType}
                    onChange={(e) => updateParams({ product_type: e.target.value || null })}
                    displayEmpty
                    sx={{ borderRadius: 2 }}
                >
                    <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                    <MenuItem value="physical">{t('search.physical', 'Physical')}</MenuItem>
                    <MenuItem value="digital">{t('search.digital', 'Digital')}</MenuItem>
                    <MenuItem value="service">{t('search.service', 'Service')}</MenuItem>
                </Select>
            </Box>

            {/* In stock only */}
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={inStockOnly}
                            onChange={(e) => updateParams({ in_stock: e.target.checked ? '1' : null })}
                        />
                    }
                    label={
                        <Typography variant="body2" fontWeight={600}>
                            {t('search.inStockOnly', 'In stock only')}
                        </Typography>
                    }
                />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ py: 4, minHeight: '80vh', backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#F9FAFB' }}>
            <Container maxWidth="lg">
                {/* Header & Breadcrumbs */}
                <Box sx={{ mb: 4 }}>
                    <Breadcrumbs
                        separator={<ChevronRight fontSize="small" />}
                        sx={{ mb: 3, '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' } }}
                    >
                        <Link
                            component="button"
                            onClick={() => navigate('/')}
                            sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', border: 'none', background: 'none', cursor: 'pointer' }}
                        >
                            <HomeIcon sx={{ fontSize: 16 }} />
                            {t('navigation.home')}
                        </Link>
                        <Typography color="text.primary" variant="body2" fontWeight="500">
                            {t('navigation.search')}
                        </Typography>
                    </Breadcrumbs>

                    {/* Search Bar & Controls */}
                    <Stack spacing={3}>
                        <Box component="form" onSubmit={handleSearchSubmit}>
                            <TextField
                                fullWidth
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={t('search.searchForProducts')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchInput && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSearchInput('');
                                                    updateParams({ q: '' });
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 3,
                                        backgroundColor: 'background.paper',
                                        fontSize: '1rem',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: 2,
                                        }
                                    }
                                }}
                            />
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
                            <Box>
                                <Typography variant="h5" fontWeight="800" sx={{ mb: 0.5 }}>
                                    {q ? `${t('search.resultsFor')}: "${q}"` : t('search.allProducts')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {pagination?.total || 0} {t('search.productsFound')}
                                </Typography>
                            </Box>

                            <Stack direction="row" spacing={2} alignItems="center">
                                {/* Mobile Filter Button */}
                                <IconButton
                                    sx={{ display: { xs: 'flex', md: 'none' } }}
                                    onClick={() => setMobileFiltersOpen(true)}
                                >
                                    <Badge badgeContent={activeFiltersCount} color="primary">
                                        <FilterList />
                                    </Badge>
                                </IconButton>

                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <Select
                                        value={sort}
                                        onChange={handleSortChange}
                                        startAdornment={<InputAdornment position="start"><Sort sx={{ fontSize: 18 }} /></InputAdornment>}
                                        sx={{ borderRadius: 2, backgroundColor: 'background.paper' }}
                                    >
                                        <MenuItem value="newest">{t('search.sort.newest')}</MenuItem>
                                        <MenuItem value="oldest">{t('search.sort.oldest', 'Oldest')}</MenuItem>
                                        <MenuItem value="price_asc">{t('search.sort.priceAsc')}</MenuItem>
                                        <MenuItem value="price_desc">{t('search.sort.priceDesc')}</MenuItem>
                                        <MenuItem value="name_asc">{t('search.sort.nameAsc')}</MenuItem>
                                        <MenuItem value="name_desc">{t('search.sort.nameDesc', 'Name ↓')}</MenuItem>
                                    </Select>
                                </FormControl>

                                {/* Per-page picker */}
                                <FormControl size="small" sx={{ minWidth: 96 }}>
                                    <Select
                                        value={String(perPage)}
                                        onChange={(e) => updateParams({ per_page: e.target.value })}
                                        sx={{ borderRadius: 2, backgroundColor: 'background.paper' }}
                                    >
                                        <MenuItem value="12">12</MenuItem>
                                        <MenuItem value="24">24</MenuItem>
                                        <MenuItem value="48">48</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </Stack>
                </Box>

                <Grid container spacing={3}>
                    {/* Filters Sidebar - Desktop */}
                    <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                position: 'sticky',
                                top: 100,
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white'
                            }}
                        >
                            <FiltersContent />
                        </Paper>
                    </Grid>

                    {/* Mobile Filters Drawer */}
                    <Drawer
                        anchor={lng === 'ar' ? 'right' : 'left'}
                        open={mobileFiltersOpen}
                        onClose={() => setMobileFiltersOpen(false)}
                        sx={{ display: { xs: 'block', md: 'none' } }}
                    >
                        <Box sx={{ width: 300, p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                                <Typography variant="h6" fontWeight="700">
                                    {t('search.filters')}
                                </Typography>
                                <IconButton onClick={() => setMobileFiltersOpen(false)}>
                                    <Close />
                                </IconButton>
                            </Stack>
                            <FiltersContent />
                        </Box>
                    </Drawer>

                    {/* Results Container */}
                    <Grid item xs={12} md={9}>
                        {/* Active Filter Chips */}
                        {activeFiltersCount > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                {departmentSlugs && departmentSlugs.split(',').map(slug => {
                                    const dept = departments.find(d => d.slug === slug);
                                    return (
                                        <Chip
                                            key={slug}
                                            label={dept?.name?.[lng] || dept?.name?.en || slug}
                                            onDelete={() => handleDepartmentToggle(slug)}
                                            size="small"
                                            sx={{ borderRadius: 2, fontWeight: 600 }}
                                        />
                                    );
                                })}
                                {categorySlugs && categorySlugs.split(',').map(slug => {
                                    const cat = allCategories.find(c => c.slug === slug);
                                    return (
                                        <Chip
                                            key={slug}
                                            label={cat?.name?.[lng] || cat?.name?.en || slug}
                                            onDelete={() => handleCategoryToggle(slug)}
                                            size="small"
                                            sx={{ borderRadius: 2, fontWeight: 600 }}
                                        />
                                    );
                                })}
                                {brandSlugs && brandSlugs.split(',').map(slug => {
                                    const brand = allBrands.find(b => b.slug === slug);
                                    return (
                                        <Chip
                                            key={slug}
                                            label={brand?.name?.[lng] || brand?.name?.en || slug}
                                            onDelete={() => handleBrandToggle(slug)}
                                            size="small"
                                            sx={{ borderRadius: 2, fontWeight: 600 }}
                                        />
                                    );
                                })}
                                {tagSlugs && tagSlugs.split(',').map(slug => {
                                    const tag = allTags.find(t => t.slug === slug);
                                    return (
                                        <Chip
                                            key={slug}
                                            label={tag?.name?.[lng] || tag?.name?.en || slug}
                                            onDelete={() => handleTagToggle(slug)}
                                            size="small"
                                            color="secondary"
                                            sx={{ borderRadius: 2, fontWeight: 600 }}
                                        />
                                    );
                                })}
                                {(minPrice || maxPrice) && (
                                    <Chip
                                        label={`${formatMoney(minPrice || priceBounds.lo)} - ${maxPrice ? formatMoney(maxPrice) : '∞'}`}
                                        onDelete={() => updateParams({ min_price: null, max_price: null })}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderRadius: 2, fontWeight: 600 }}
                                    />
                                )}
                                {productType && (
                                    <Chip
                                        label={t(`search.${productType}`, productType)}
                                        onDelete={() => updateParams({ product_type: null })}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderRadius: 2, fontWeight: 600 }}
                                    />
                                )}
                                {inStockOnly && (
                                    <Chip
                                        label={t('search.inStockOnly', 'In stock only')}
                                        onDelete={() => updateParams({ in_stock: null })}
                                        size="small"
                                        color="success"
                                        sx={{ borderRadius: 2, fontWeight: 600 }}
                                    />
                                )}
                            </Stack>
                        )}

                        {isLoading ? (
                            <Grid container spacing={3}>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <Grid item xs={12} sm={6} lg={4} key={i}>
                                        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3, mb: 1 }} />
                                        <Skeleton variant="text" width="80%" height={28} sx={{ mb: 0.5 }} />
                                        <Skeleton variant="text" width="40%" height={24} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : searchResults.length > 0 ? (
                            <>
                                <Grid container spacing={3}>
                                    {searchResults.map((product) => (
                                        <Grid item xs={12} sm={6} lg={4} key={product.id}>
                                            <ProductCard
                                                product={product}
                                                onClick={() => navigate(`/products/${product.slug}`)}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Pagination */}
                                {pagination && pagination.last_page > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                                        <Pagination
                                            count={pagination.last_page}
                                            page={page}
                                            onChange={(_, value) => {
                                                const newParams = new URLSearchParams(searchParams);
                                                newParams.set('page', value.toString());
                                                setSearchParams(newParams);
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }}
                                            color="primary"
                                            shape="rounded"
                                            size="large"
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    fontWeight: 600,
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                            </>
                        ) : (
                            /* Empty State */
                            <Paper
                                elevation={0}
                                sx={{
                                    textAlign: 'center',
                                    py: 12,
                                    px: 4,
                                    borderRadius: 4,
                                    border: `2px dashed ${theme.palette.divider}`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                <Box sx={{ mb: 3, opacity: 0.5 }}>
                                    <SearchOff sx={{ fontSize: 80, color: 'text.secondary' }} />
                                </Box>
                                <Typography variant="h5" fontWeight="800" gutterBottom>
                                    {t('search.noMatches')}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                                    {t('search.noMatchesMessage')}
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<ClearAll />}
                                    onClick={clearAllFilters}
                                    sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 700 }}
                                >
                                    {t('search.resetAndTryAgain')}
                                </Button>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Search;
